import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Search, Volume2, Plus, BookOpen, Tag, Globe } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const KeywordsComponent = ({ storyId, keywords = [], onAddToTraining }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedKeyword, setExpandedKeyword] = useState(null);
  const { toast } = useToast();

  const categories = ['all', 'Substantiv', 'Verb', 'Adjektiv', 'Adverb', 'Präposition'];
  
  const filteredKeywords = keywords.filter(keyword => {
    const matchesSearch = keyword.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         keyword.translation.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         keyword.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || keyword.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleAddToTraining = (keyword) => {
    if (onAddToTraining) {
      onAddToTraining({
        type: 'keyword',
        word: keyword.word,
        translation: keyword.translation,
        definition: keyword.definition,
        category: keyword.category,
        level: keyword.level,
        examples: keyword.examples
      });
    }
    
    toast({
      title: "Wort hinzugefügt",
      description: `"${keyword.word}" wurde zum Training hinzugefügt.`,
    });
  };

  const speakWord = (word, language = 'de') => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = language === 'de' ? 'de-DE' : 'en-US';
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-100 text-green-800',
      'A2': 'bg-blue-100 text-blue-800',
      'B1': 'bg-yellow-100 text-yellow-800',
      'B2': 'bg-orange-100 text-orange-800',
      'C1': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Substantiv': 'bg-purple-100 text-purple-800',
      'Verb': 'bg-blue-100 text-blue-800',
      'Adjektiv': 'bg-green-100 text-green-800',
      'Adverb': 'bg-yellow-100 text-yellow-800',
      'Präposition': 'bg-red-100 text-red-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (keywords.length === 0) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-8 text-center">
          <BookOpen className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Keine Schlüsselwörter verfügbar
          </h3>
          <p className="text-slate-600">
            Für diese Geschichte wurden noch keine Schlüsselwörter definiert.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Suche nach Wörtern, Übersetzungen oder Definitionen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? "bg-[#4ECDC4] hover:bg-[#45b7aa]" : ""}
                >
                  {category === 'all' ? 'Alle Kategorien' : category}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Keywords List */}
      <div className="space-y-4">
        {filteredKeywords.map((keyword, index) => (
          <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="flex items-center space-x-3">
                        <h3 className="text-xl font-bold text-slate-900">
                          {keyword.word}
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => speakWord(keyword.word, 'de')}
                        >
                          <Volume2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <Globe className="h-4 w-4 text-slate-400" />
                        <span className="text-lg text-slate-600">
                          {keyword.translation}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => speakWord(keyword.translation, 'en')}
                        >
                          <Volume2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Badge className={getLevelColor(keyword.level)}>
                      {keyword.level}
                    </Badge>
                    <Badge className={getCategoryColor(keyword.category)}>
                      <Tag className="h-3 w-3 mr-1" />
                      {keyword.category}
                    </Badge>
                    <Button
                      size="sm"
                      className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                      onClick={() => handleAddToTraining(keyword)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Hinzufügen
                    </Button>
                  </div>
                </div>

                {/* Definition */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-slate-800 leading-relaxed">
                    {keyword.definition}
                  </p>
                </div>

                {/* Examples */}
                {keyword.examples && keyword.examples.length > 0 && (
                  <div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedKeyword(
                        expandedKeyword === index ? null : index
                      )}
                      className="text-[#4ECDC4] hover:text-[#45b7aa]"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      {expandedKeyword === index ? 'Beispiele ausblenden' : `${keyword.examples.length} Beispiele anzeigen`}
                    </Button>
                    
                    {expandedKeyword === index && (
                      <div className="mt-3 space-y-2">
                        {keyword.examples.map((example, exampleIndex) => (
                          <div key={exampleIndex} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                            <p className="text-slate-800 flex-1">
                              {example}
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => speakWord(example, 'de')}
                            >
                              <Volume2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Related Words */}
                {keyword.related && keyword.related.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="text-sm font-medium text-slate-900 mb-2">
                        Verwandte Wörter:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {keyword.related.map((relatedWord, relatedIndex) => (
                          <Badge
                            key={relatedIndex}
                            variant="outline"
                            className="cursor-pointer hover:bg-slate-100"
                            onClick={() => setSearchTerm(relatedWord)}
                          >
                            {relatedWord}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredKeywords.length === 0 && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <Search className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              Keine Ergebnisse gefunden
            </h3>
            <p className="text-slate-600">
              Versuchen Sie einen anderen Suchbegriff oder wählen Sie eine andere Kategorie.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-[#4ECDC4] to-[#45b7aa]">
        <CardContent className="p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-bold text-lg mb-1">
                {filteredKeywords.length} Schlüsselwörter
              </h4>
              <p className="opacity-90">
                Erweitern Sie Ihren Wortschatz mit diesen wichtigen Begriffen
              </p>
            </div>
            <BookOpen className="h-8 w-8 opacity-80" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default KeywordsComponent;